import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import RoomBreakdownDetails from './RoomBreakdownDetails'
import RoomBreakdownLoadingSkeleton from './RoomBreakdownLoadingSkeleton'
import useOfferMetaData from '../../../../hooks/Offers/useOfferMetaData'
import { isBedbankSearchRoom, isLEOffer, isReservationSearchRoom } from 'lib/offer/offerTypes'
import useBedbankRoomBreakdown from '../../../../hooks/useBedbankRoomBreakdown'
import { getRoomBreakdown, getPackageRoomBreakdown, mapSearchOccupancyToOccupants } from 'lib/search/searchUtils'
import { excludeNullOrUndefined } from 'checkout/utils'
import { useMaldivesOffersPricingContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesOffersPricing'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function SearchTileRoomBreakdown({
  offer,
  filters,
}: Props) {
  const intl = useIntl()
  const metaData = useOfferMetaData(offer.id, filters)

  // Try to get package IDs from Maldives pricing context (for multi-room allocation)
  const maldivesPricing = useMaldivesOffersPricingContext()
  const maldivesPackageIds = maldivesPricing.pricingByOfferId[offer.id]?.packageIds

  const recommendedForText = useMemo(() => {
    const numberOfAdults = filters?.rooms?.reduce((sum, room) => sum + room.adults, 0) ?? 0
    const numberOfChildren = filters?.rooms?.reduce((sum, room) => sum + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0), 0) ?? 0
    // we don't want to concatenate the strings here cuz it breaks RTL languages but can't figure another way of doing this without separating 2 clauses of the sentence
    // eslint-disable-next-line formatjs/no-multiple-plurals
    return intl.formatMessage({
      id: 'hFKgypXkRd',
      defaultMessage: 'Recommended for {numberOfAdults, plural, one {# adult} other {# adults}}{numberOfChildren, plural, =0 {} one {, # child} other {, # children}}',
      description: 'Room breakdown recommendation showing adult and optional children guest count on search tile',
    }, { numberOfAdults, numberOfChildren })
  }, [filters?.rooms, intl])

  const leRoomsList = useMemo(() => {
    const packages = isLEOffer(offer) ? offer.packages : []

    // If we have Maldives package IDs, use them directly via package ID
    if (maldivesPackageIds && maldivesPackageIds.length > 0) {
      return getPackageRoomBreakdown(maldivesPackageIds, packages)
    }

    // Otherwise, get room breakdown from metadata rooms
    const metaDataRooms = metaData?.rooms.filter(isReservationSearchRoom) ?? []
    return getRoomBreakdown(metaDataRooms, packages)
  }, [maldivesPackageIds, metaData?.rooms, offer])

  const bedbankRooms = useMemo(() =>
    metaData?.rooms
      .filter(isBedbankSearchRoom)
      .map(r => (r.rateId ?
          {
            rateId: r.rateId,
            propertyId: r.propertyId,
            occupancy: mapSearchOccupancyToOccupants(r.occupancy),
          }
        : undefined))
      .filter(excludeNullOrUndefined) ?? [],
  [metaData?.rooms])

  const { rooms: bedbankRoomsList, isLoading: isBedbankLoading } = useBedbankRoomBreakdown(bedbankRooms, filters)

  const allRooms = useMemo(() =>
    [...leRoomsList, ...bedbankRoomsList],
  [leRoomsList, bedbankRoomsList])

  if (isBedbankLoading) return <RoomBreakdownLoadingSkeleton />

  if (allRooms.length === 0) return null

  return (
    <RoomBreakdownDetails
      recommendedForText={recommendedForText}
      rooms={allRooms}
    />
  )
}

export default SearchTileRoomBreakdown
